/**
 * Envío LMX-T · trabajos de LMX y secciones del canvas (lógica pura, con tests).
 *
 * Un pedido de producción es un TRABAJO con número y color (hasta 2 a la vez
 * por proyecto, hasta 3 en espera). Las reglas las aplica el servidor; acá
 * sólo se decide qué mostrar y cuándo arrancar el próximo pedido en espera.
 */

export const ACTIVE = ['armando', 'esperando', 'generando'];
export const CLOSED = ['listo', 'fallido', 'cancelado'];

/** ¿Hay que arrancar el próximo pedido en espera? Sólo con LMX libre y sin
 *  nada que la persona tenga que responder (pregunta, aprobación, opciones). */
export const nextToDispatch = ({ snap, busy, pendingConfirm, cards }) => {
  if (busy || pendingConfirm || !snap || snap.turn_running) return null;
  const waiting = (cards || []).some((c) => ['question', 'jobs_full'].includes(c?.type));
  if (waiting) return null;
  const next = snap.next_queued;
  return next && next.status === 'en_cola' && next.request ? next : null;
};

/** Avance de un trabajo que genera: suma las corridas vivas que son suyas. */
export const jobProgress = (job, runs) => {
  const mine = (runs || []).filter((r) => (job?.run_ids || []).includes(r.run_id));
  if (!mine.length) return null;
  const total = mine.reduce((n, r) => n + ((r.steps || []).length || 0), 0);
  const done = mine.reduce((n, r) => n + ((r.completed_nodes || []).length || 0), 0);
  return total ? { done, total } : null;
};

/** Botones de cada fila de la lista de trabajos. */
export const jobActions = (job, { canResume = true } = {}) => {
  if (!job) return [];
  if (ACTIVE.includes(job.status)) return ['pause'];
  if (job.status === 'pausado') return canResume ? ['resume', 'remove'] : ['remove'];
  if (job.status === 'fallido') return canResume ? ['resume', 'remove'] : ['remove'];
  return ['remove'];                        // en_cola, listo, cancelado
};

export const ACTION_LABEL = { pause: 'Pausar', resume: 'Retomar', remove: 'Quitar' };

/** Texto del estado, con el avance si está generando. */
export const jobLine = (job, progress) => {
  if (!job) return '';
  if (job.status === 'generando' && progress) {
    return `Generando · ${progress.done} de ${progress.total} listos`;
  }
  return {
    en_cola: 'En espera · arranca solo al terminar uno',
    armando: 'Armando el flujo',
    esperando: 'Esperando tu OK',
    generando: 'Generando',
    pausado: 'Pausado · lo hecho queda guardado',
    listo: 'Listo',
    fallido: 'Terminó con fallas',
    cancelado: 'Cancelado',
  }[job.status] || job.label || job.status;
};

/** Encabezado «Trabajos 2 de 2 · 1 en espera (máx. 3)». */
export const jobsHeader = (snap) => {
  if (!snap) return '';
  const max = snap.max_active || 2;
  const q = snap.queued || 0;
  return `${snap.active || 0} de ${max}${q ? ` · ${q} en espera (máx. ${snap.max_queued || 3})` : ''}`;
};

// ── Secciones del canvas ────────────────────────────────────────────────
export const SECTION_PREFIX = 'sec:';
export const isSectionId = (id) => String(id || '').startsWith(SECTION_PREFIX);
export const sectionIdOf = (nodeId) => String(nodeId).slice(SECTION_PREFIX.length);

/** Secciones → nodos de React Flow (sólo visuales: no van al store del flujo). */
export const sectionsToNodes = (sections) => (sections || []).map((s) => ({
  id: `${SECTION_PREFIX}${s.id}`,
  type: 'canvasSection',
  position: { x: s.x, y: s.y },
  data: { section: s },
  style: { width: s.w, height: s.h },
  zIndex: -1,
  draggable: true,
  selectable: false,
  connectable: false,
  focusable: false,
  dragHandle: '.cs-head',
}));

/** Recuadro que encierra nodos medidos por React Flow (para «Marcar sección»). */
export const boxAround = (nodes, { pad = 40, header = 44 } = {}) => {
  const boxes = (nodes || [])
    .filter((n) => n?.position && Number.isFinite(n.position.x) && Number.isFinite(n.position.y))
    .map((n) => {
      const x = n.positionAbsolute?.x ?? n.position.x;
      const y = n.positionAbsolute?.y ?? n.position.y;
      return [x, y, x + (n.width || 340), y + (n.height || 320)];
    });
  if (!boxes.length) return null;
  const x0 = Math.min(...boxes.map((b) => b[0])) - pad;
  const y0 = Math.min(...boxes.map((b) => b[1])) - pad - header;
  const x1 = Math.max(...boxes.map((b) => b[2])) + pad;
  const y1 = Math.max(...boxes.map((b) => b[3])) + pad;
  return { x: Math.round(x0), y: Math.round(y0), w: Math.round(x1 - x0), h: Math.round(y1 - y0) };
};

/** Divide los cambios de React Flow: los de secciones y los del flujo. */
export const splitChanges = (changes) => {
  const sections = [];
  const flow = [];
  (changes || []).forEach((c) => (isSectionId(c?.id) ? sections : flow).push(c));
  return { sections, flow };
};

/** Cambios de React Flow sobre secciones → qué actualizar y qué guardar.
 *  Mover: se actualiza en vivo y se guarda al soltar. */
export const sectionMoves = (changes) => (changes || [])
  .filter((c) => isSectionId(c?.id) && c.type === 'position')
  .map((c) => ({
    id: sectionIdOf(c.id),
    patch: c.position ? { x: Math.round(c.position.x), y: Math.round(c.position.y) } : null,
    save: c.dragging === false || (!!c.position && c.dragging == null),
  }));

export const SECTION_COLORS = ['#4ea2ff', '#a78bfa', '#36d39a', '#f2b84b', '#f472b6', '#22d3ee'];
